import type { ReactNode, HTMLAttributes } from 'react';

import styles from './errorBanner.module.scss';

export type ErrorBannerProps = Omit<HTMLAttributes<HTMLDivElement>, 'title'> & {
  /** The main heading for the error */
  title: string;
  /** Detailed error message or description */
  message?: ReactNode;
  /** Called when the close button is clicked. If omitted, no close button is shown. */
  onDismiss?: () => void;
};

/**
 * A styled error banner with icon, title, message, and optional dismiss button.
 * Use for inline form errors, API failures, or validation messages.
 */
export function ErrorBanner({
  title,
  message,
  onDismiss,
  className,
  ...rest
}: ErrorBannerProps) {
  const classes = [styles.errorBanner, className].filter(Boolean).join(' ');

  return (
    <div className={classes} role="alert" {...rest}>
      <svg
        className={styles.errorBanner__icon}
        viewBox="0 0 20 20"
        fill="currentColor"
        aria-hidden="true"
      >
        <path
          fillRule="evenodd"
          d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z"
          clipRule="evenodd"
        />
      </svg>
      <div className={styles.errorBanner__content}>
        <p className={styles.errorBanner__title}>{title}</p>
        {message && <p className={styles.errorBanner__message}>{message}</p>}
      </div>
      {onDismiss && (
        <button
          type="button"
          className={styles.errorBanner__close}
          onClick={onDismiss}
          aria-label="Dismiss error"
        >
          <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
          </svg>
        </button>
      )}
    </div>
  );
}
